import React, { useState } from "react";
import { TextField, Switch, FormControlLabel, Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import useUpdateData from "../../hooks/useUpdateData";
import { IControl } from "./types";
import { useTranslation } from "react-i18next";
import { TOption } from "../../Components/Select";
import { IconSets } from "./IconSets";
import FormikSelect from "../../Components/FormikSelect";
import ToggleIconSetPreview from "./ToggleIconSetPreview";
import DialogButtons from "../../Components/DialogButtons";

interface IControlFormProps {
  control: IControl;
}

const ControlForm = (props: IControlFormProps) => {
  const { control } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const iconSetOptions: TOption<string>[] = Object.keys(IconSets)
    .sort()
    .map((iconSetName) => {
      return {
        value: iconSetName,
      };
    });

  const handleCancel = () => {
    showMessage({
      text: control.name
        ? t("Editing control CONTROL cancelled", { control })
        : t("Creating new control cancelled"),
      severity: "info",
    });
    navigate("/");
  };

  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      name: control.name,
      is_enabled: control.is_enabled,
      icon_set_name: control.icon_set_name,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const allValues = { ...control, ...values };
      updateHandler(
        t("Control WHAT", { what: allValues.name }),
        `/controls/${control.id}`,
        allValues
      );
      navigate("/");
    },
  });
  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <Stack spacing={2} alignItems="flex-start">
          <TextField
            fullWidth
            autoFocus
            margin="normal"
            id="name"
            label={t("Name")}
            type="text"
            disabled={isSubmitting}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
          ></TextField>
          <FormControlLabel
            control={
              <Switch
                id="is_enabled"
                name="is_enabled"
                checked={formik.values.is_enabled}
                onChange={formik.handleChange}
              />
            }
            label={t("Enabled")}
            labelPlacement="start"
          />
          {formik.values.icon_set_name && (
            <Box
              gap={1}
              display="flex"
              flexDirection="row"
              style={{ width: "100%" }}
            >
              <FormikSelect
                label={t("Icon set")}
                name="icon_set_name"
                options={iconSetOptions}
                data-cy="message-picker-select-level"
                fullWidth
              />
              <Box paddingTop={1}>
                <ToggleIconSetPreview
                  icon_set_name={formik.values.icon_set_name}
                />
              </Box>
            </Box>
          )}
        </Stack>
        <DialogButtons
          isSubmitting={isSubmitting}
          submitButtonLabel="Save"
          resetCallback={handleCancel}
        />
      </FormikProvider>
    </form>
  );
};

export default ControlForm;
